import { useMemo } from "react";
import { logEvent } from "react-migration/lib/util/logEvent";
import { SelectionType, mapStore } from "src/js/stores/map/store";
import { CollapsibleConsiderationsCard } from "../../Bundle/Workbench/AssessmentTab/CollapsibleConsiderationsCard";
import type { LayerTypeSelectionSidebarProps } from "../../types";
import { SaveSiteLayer } from "react-migration/domains/sites/typings/SaveSiteLayer";
import { SiteButton } from "./SiteButton";
import { intersectionSignificanceOrdering } from "../../geometry_utilities";
import { useSiteStagesById } from "react-migration/domains/sites/hooks/useSiteStages";
import { useSnapshot } from "valtio";
import { useIntersectingSites } from "./useIntersectingSites";

export function SitesSelectionSidebar({ layer, cardExpanded, selection, onSelection }: LayerTypeSelectionSidebarProps) {
  const { sites } = useIntersectingSites(selection?.feature?.geometry);
  const stagesById = useSiteStagesById();
  const { settings } = useSnapshot(mapStore);
  const { showHiddenSites, showSharedSites } = settings;

  const filteredSites = useMemo(() => {
    if (!selection.feature?.geometry) return [];
    const selectedSiteIDs = [selection, ...(selection.subselections || [])]
      .filter((s) => s.type === SelectionType.SITE || s.type === SelectionType.SHARED_SITE)
      .map((s) => s.id);

    const filtered = sites
      .filter((site) => !selectedSiteIDs.includes(site.id))
      .filter((site) => {
        const stage = stagesById[site.properties._stage._id];
        const siteHidden = site.properties.hidden || stage?.hidden;
        const hideBecauseHidden = siteHidden && !showHiddenSites;
        const hideBecauseShared = site.properties.shared && !showSharedSites;
        return !hideBecauseHidden && !hideBecauseShared;
      });

    const orderedSites = intersectionSignificanceOrdering(
      selection.feature.geometry,
      filtered,
      (site) => site.geometry
    );
    return orderedSites;
  }, [selection, sites, stagesById, showHiddenSites, showSharedSites]);

  if (!filteredSites?.length) return null;

  return (
    <CollapsibleConsiderationsCard
      heading={layer.title}
      name={layer.title}
      count={filteredSites.length}
      defaultExpanded={cardExpanded}
    >
      <div className="m-2 mt-1 overflow-hidden bg-white border divide-y rounded-sm border-border-action divide-border-base-divider">
        {filteredSites.map((site) => (
          <SiteButton
            key={site.id}
            site={site}
            onClick={() => {
              logEvent("Part of Saved Site Click", {
                layer: SaveSiteLayer.Constraints,
                id: site.id,
              });

              onSelection({
                type: SelectionType.SITE,
                id: site.id,
                savable: false,
                feature: site,
              });
            }}
          />
        ))}
      </div>
    </CollapsibleConsiderationsCard>
  );
}
